import {
  Accordion,
  AlignItems,
  Body3,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Heading6,
  Icon,
  IconVariant,
  Image,
  JustifyContent,
  Space,
  Text,
  ZIndex,
} from '@gousto-internal/citrus-react'
import { queueTrackingEvent } from '@library/tracking-integration'
import React from 'react'
import { CuratedBoxImageLayout } from '../CuratedBoxImageLayout'
import { RecipeTag } from '../RecipeTag/RecipeTag'
import { AddIcon, AddIconTile } from './CuratedBoxRecipeTile.styled'

type HandleOnClick = (
  curatedBoxRecipes: string[],
  curatedBoxCollectionId: string,
  curatedBoxName: string,
  index: number,
) => void

export type TopInStockRecipe = {
  recipeId: string
  recipeImageSrc: string
  title: string
  prepTimes: number
}

export type CuratedBox = {
  type: string
  id: string
  curatedBoxCollectionId: string
  title: string
  tags: string[]
  topInStockRecipes: TopInStockRecipe[]
}

type CuratedBoxRecipeProps = {
  index: number
  curatedBoxData: CuratedBox
  handleOnClick: HandleOnClick
  numPortions: 1 | 2 | 3 | 4 | 5
}

type CuratedBoxRecipeTileProps = {
  tags: string[]
  curatedBoxCollectionId: string
  index: number
  handleOnClick: HandleOnClick
  topInStockRecipes: TopInStockRecipe[]
  title: string
  isExpanded: boolean
  numPortions: 1 | 2 | 3 | 4 | 5
}

type IconButtonProps = {
  handleOnKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
  handleClick: (event: React.MouseEvent<HTMLElement>) => void
}

const RecipeTagRow = ({ recipeTags }: { recipeTags: string[] }) => (
  <Box paddingH={[2, null]} display={Display.Flex}>
    {recipeTags.map((recipeTag) => (
      <Box paddingRight={[2, null, 1]} key={recipeTag}>
        <RecipeTag key={recipeTag} tagText={recipeTag} />
      </Box>
    ))}
  </Box>
)

const IconButton = ({ handleClick, handleOnKeyDown }: IconButtonProps) => (
  <Box
    data-testid="addCuratedBoxIconButton"
    tabIndex={0}
    onClick={handleClick}
    onKeyDown={handleOnKeyDown}
    borderRadius={1.5}
    bg={Color.White}
    paddingH={1}
    paddingV={1}
  >
    <AddIconTile>
      <AddIcon size={5} name="add" />
    </AddIconTile>
  </Box>
)

const CuratedBoxRecipePreviewLineItem = ({
  title,
  prepTimes,
  recipeImageSrc,
  recipeId,
}: TopInStockRecipe) => (
  <Box data-testid="curatedBoxPreviewLineItem" paddingBottom={2}>
    <Box paddingH={1} display={Display.Flex} flexDirection={FlexDirection.Row}>
      <Box paddingH={2}>
        <Image fit="cover" height="64px" width="116px" alt={recipeId} src={recipeImageSrc} />
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.SpaceBetween}
      >
        <Heading6 size={1} fontFamily={FontFamily.Bold}>
          {title}
        </Heading6>
        <Body3>{prepTimes} mins</Body3>
      </Box>
    </Box>
  </Box>
)

const CuratedBoxRecipeTile = ({
  handleOnClick,
  index,
  title,
  tags,
  topInStockRecipes,
  curatedBoxCollectionId,
  isExpanded,
  numPortions,
}: CuratedBoxRecipeTileProps) => {
  const curatedBoxImgUrls = topInStockRecipes.map((item) => item?.recipeImageSrc)

  if (isExpanded) {
    const trackingData = {
      action: 'click_curated_box_expand',
      properties: {
        component: 'curated_box_expand',
        boxName: title,
      },
    }
    queueTrackingEvent<typeof trackingData>(trackingData)
  }

  const handleEvent = () => {
    const recipeIds = topInStockRecipes.map((recipe) => recipe?.recipeId) as string[]
    handleOnClick(recipeIds, curatedBoxCollectionId, title, index)
  }
  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    event.stopPropagation()
    if (event.key === 'Enter') {
      handleEvent()
    }
  }

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    handleEvent()
  }

  return (
    <Box data-testid="curatedBoxRecipeTile">
      <Box position={DisplayPosition.Relative}>
        <CuratedBoxImageLayout
          numPortions={numPortions}
          curatedBoxImgURLS={curatedBoxImgUrls as string[]}
        />
        <Box
          position={DisplayPosition.Absolute}
          left="4px"
          bottom="-24px"
          right="4px"
          display={Display.Flex}
          alignItems={AlignItems.Center}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.SpaceBetween}
        >
          <Box>
            <RecipeTagRow recipeTags={tags} />
          </Box>
          <Box paddingRight={2}>
            <IconButton handleOnKeyDown={handleOnKeyDown} handleClick={onClick} />
          </Box>
        </Box>
      </Box>
      <Box paddingH={3}>
        <Box paddingV={2}>
          <Space size={3} />
          <Text size={3} fontFamily={FontFamily.Bold} fontWeight={FontWeight.SemiBold}>
            {title}
          </Text>
        </Box>
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        justifyContent={JustifyContent.FlexEnd}
        paddingH={3}
        paddingBottom={2}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          alignItems={AlignItems.Center}
          paddingBottom={1}
        >
          <Box>
            <Text color={Color.Secondary_400} fontFamily={FontFamily.Bold} size={2}>
              What&apos;s in the box?{' '}
            </Text>
          </Box>
          <Box>
            <Space size={1} />
            {isExpanded ? (
              <Icon variant={IconVariant.Actionable} name="chevron_up" />
            ) : (
              <Icon variant={IconVariant.Actionable} name="chevron_down" />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const CuratedBoxRecipe = ({
  curatedBoxData,
  handleOnClick,
  index,
  numPortions,
}: CuratedBoxRecipeProps) => {
  const { topInStockRecipes, tags, title, curatedBoxCollectionId } = curatedBoxData

  return (
    <Box
      boxShadow="0px 4px 20px 0px rgba(0, 0, 0, 0.15);"
      borderRadius={1}
      position={[null, null, DisplayPosition.Relative]}
      bg={Color.White}
    >
      <Accordion
        id={`curatedBoxRecipePreviewAccordionButton-${title}`}
        title={(isExpanded) => (
          <CuratedBoxRecipeTile
            title={title}
            curatedBoxCollectionId={curatedBoxCollectionId}
            handleOnClick={handleOnClick}
            tags={tags}
            index={index}
            topInStockRecipes={topInStockRecipes}
            isExpanded={isExpanded}
            numPortions={numPortions}
          />
        )}
      >
        <Box
          bg={Color.White}
          zIndex={[null, null, ZIndex.Dropdown]}
          left={[null, null, '0px']}
          right={[null, null, '0px']}
          paddingBottom={2}
          position={[null, null, DisplayPosition.Absolute]}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
        >
          {topInStockRecipes.map((topInStockRecipe) => (
            <CuratedBoxRecipePreviewLineItem
              key={topInStockRecipe.recipeId}
              title={topInStockRecipe.title}
              prepTimes={topInStockRecipe.prepTimes}
              recipeImageSrc={topInStockRecipe.recipeImageSrc}
              recipeId={topInStockRecipe.recipeId}
            />
          ))}
          <Space size={3} />
        </Box>
      </Accordion>
    </Box>
  )
}
